import type { ComponentProps, ReactNode } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { faArrowDown } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { StickToBottom, useStickToBottomContext } from 'use-stick-to-bottom';

import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/utils';

export type ConversationProps = ComponentProps<typeof StickToBottom> & {
  autoScroll?: boolean;
  messageCount?: number;
  layoutShiftKey?: string | number | boolean | null;
};

export const Conversation = ({
  className,
  autoScroll = true,
  messageCount,
  layoutShiftKey,
  children,
  ...props
}: ConversationProps) => {
  return (
    <StickToBottom
      className={cn('relative flex-1 overflow-y-hidden', className)}
      initial="smooth"
      resize="smooth"
      role="log"
      {...props}
    >
      {autoScroll && messageCount !== undefined && (
        <ConversationAutoScroll messageCount={messageCount} layoutShiftKey={layoutShiftKey} />
      )}
      {children as ReactNode}
    </StickToBottom>
  );
};

function ConversationAutoScroll({
  messageCount,
  layoutShiftKey,
}: {
  messageCount: number;
  layoutShiftKey?: string | number | boolean | null;
}) {
  const { scrollToBottom } = useStickToBottomContext();
  const prevMessageCountRef = useRef(messageCount);
  const prevLayoutShiftKeyRef = useRef(layoutShiftKey);

  useEffect(() => {
    const hasNewMessage = messageCount > prevMessageCountRef.current;
    const hasLayoutShift = layoutShiftKey !== prevLayoutShiftKeyRef.current;

    if (hasNewMessage || hasLayoutShift) {
      scrollToBottom();
    }

    prevMessageCountRef.current = messageCount;
    prevLayoutShiftKeyRef.current = layoutShiftKey;
  }, [layoutShiftKey, messageCount, scrollToBottom]);

  return null;
}

export type ConversationContentProps = ComponentProps<typeof StickToBottom.Content>;

export const ConversationContent = ({ className, ...props }: ConversationContentProps) => (
  <StickToBottom.Content className={cn('flex flex-col gap-4 p-4', className)} {...props} />
);

export type ConversationEmptyStateProps = ComponentProps<'div'> & {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
};

export const ConversationEmptyState = ({
  className,
  title = 'No messages yet',
  description = 'Start a conversation to see messages here',
  icon,
  children,
  ...props
}: ConversationEmptyStateProps) => (
  <div
    className={cn(
      'flex size-full flex-col items-center justify-center gap-3 p-8 text-center',
      className,
    )}
    {...props}
  >
    {children ?? (
      <>
        {icon && <div className="text-muted-foreground">{icon}</div>}
        <div className="space-y-1">
          <h3 className="font-medium text-sm">{title}</h3>
          {description && <p className="text-muted-foreground text-sm">{description}</p>}
        </div>
      </>
    )}
  </div>
);

export type ConversationScrollButtonProps = ComponentProps<typeof Button>;

export const ConversationScrollButton = ({
  className,
  ...props
}: ConversationScrollButtonProps) => {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();

  const handleScrollToBottom = useCallback(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  return (
    !isAtBottom && (
      <Button
        className={cn('absolute bottom-4 left-[50%] translate-x-[-50%] rounded-full', className)}
        colorScheme="secondary"
        onClick={handleScrollToBottom}
        size="icon"
        variant="outline"
        {...props}
      >
        <Icon icon={faArrowDown} />
      </Button>
    )
  );
};
